import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Tab, Tabs, Typography} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import dayjs, {Dayjs} from 'dayjs'
import Card from 'src/components-new/layout/Card'
import ChartCard from 'src/components-new/data/ChartCard'
import EmptyState from 'src/components-new/layout/EmptyState'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import apiClient from 'src/services/apiClient'
import {
  ADHERENCE_DEFAULT_RANGE_DAYS,
  AdherenceFilters,
  AdherenceLabels,
  AdherenceResponse,
  AdherenceSection,
  AdherenceTherapyType,
  buildAdherenceByTherapyOptions,
  buildAdherenceFilterItems,
  buildAdherenceTimelineOptions,
  formatAdherencePercentage,
  isSecondaryAdherenceEnabled,
  resolveAdherenceFiltersConfig,
  resolveAdherenceLabels,
  resolveAdherenceTabs,
  toAdherenceFilters,
} from 'src/utils/adherenceCharts'

type PerTab<T> = Record<AdherenceTherapyType, T>

// Same window the backend falls back to, made explicit so the "Data" filter shows it pre-filled
// and "Azzera" brings it back instead of clearing to an unbounded range.
function defaultRange(): [Dayjs, Dayjs] {
  return [dayjs().subtract(ADHERENCE_DEFAULT_RANGE_DAYS, 'day'), dayjs()]
}

function defaultFilters(): AdherenceFilters {
  const [from, to] = defaultRange()
  return {dateFrom: from.startOf('day').toISOString(), dateTo: to.endOf('day').toISOString()}
}

function formatDate(iso: string): string {
  return dayjs(iso).format('DD/MM/YYYY')
}

function TotalsCard({section, labels}: {section: AdherenceSection; labels: AdherenceLabels}): JSX.Element {
  const items = [
    {label: labels.adherence, value: formatAdherencePercentage(section.percentage)},
    {label: labels.completedAssumptions, value: section.completed},
    {label: labels.expectedAssumptions, value: section.total},
  ]

  return (
    <Card title={labels.totalTitle}>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 2}}>
        {items.map(item => (
          <Box key={item.label} sx={{border: `1px solid ${NEUTRAL_SCALE[200]}`, borderRadius: 1, px: 2, py: 1.5}}>
            <Typography sx={{fontSize: 12, color: 'text.secondary', textTransform: 'uppercase'}}>
              {item.label}
            </Typography>
            <Typography sx={{fontSize: 24, fontWeight: 600}}>{item.value}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  )
}

function SectionCharts({section, granularity, labels}: {
  section: AdherenceSection
  granularity: AdherenceResponse['granularity']
  labels: AdherenceLabels
}): JSX.Element {
  const theme = useTheme()
  const color = theme.palette.primary.main

  if (section.total === 0) {
    return (
      <EmptyState
        title="Nessuna assunzione prevista"
        description="Non ci sono assunzioni previste per i filtri selezionati. Modifica i filtri per vedere l'aderenza."
      />
    )
  }

  return (
    <Box sx={{display: 'grid', gap: 2}}>
      <TotalsCard section={section} labels={labels} />
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
        <ChartCard title={labels.timelineTitle} options={buildAdherenceTimelineOptions(section, granularity, color)} />
        <ChartCard title={labels.byTherapyTitle} options={buildAdherenceByTherapyOptions(section, color)} />
      </Box>
    </Box>
  )
}

// Analytics page of the patient / insurance user: adherence to their primary and secondary
// therapies, i.e. COMPLETED assumptions over every assumption due in the selected range.
export default function AnalyticsNew(): JSX.Element {
  const analyticsConfigurations = useAppSelector(state => state.pspConfigReducer.data.analyticsConfigurations)
  const configuredFilters = analyticsConfigurations?.filtersConfig
  const sectionsConfig = analyticsConfigurations?.sections
  const labels = useMemo(() => resolveAdherenceLabels(analyticsConfigurations?.labels), [analyticsConfigurations?.labels])
  const secondaryEnabled = isSecondaryAdherenceEnabled(sectionsConfig)
  // Each tab has its own filters (and its own Filters drawer below): the drugs of the primary
  // therapies are not those of the secondary ones, so a drug picked on one tab must not carry over.
  const [filtersByTab, setFiltersByTab] = useState<PerTab<AdherenceFilters>>(() => ({primary: defaultFilters(), secondary: defaultFilters()}))
  // options of each tab's "Farmaco" filter, from its last response
  const [drugNamesByTab, setDrugNamesByTab] = useState<PerTab<string[]>>({primary: [], secondary: []})
  // tagged with the tab it was fetched for, so switching tab never shows the other one's numbers
  const [data, setData] = useState<{therapyType: AdherenceTherapyType; response: AdherenceResponse}>()
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [activeTab, setActiveTab] = useState<AdherenceTherapyType>('primary')
  // undefined while still unknown
  const [hasSecondaryTherapies, setHasSecondaryTherapies] = useState<boolean>()

  useEffect(() => {
    // A disabled section is never shown, whatever the patient has
    if (!secondaryEnabled) {
      setHasSecondaryTherapies(false)
      return
    }
    // Same rule as DiaryNew: the "Terapie Accessorie" tab only exists for a patient who has some.
    apiClient.getSecondaryTherapies(() => {})
      .then((therapies: unknown) => setHasSecondaryTherapies(Array.isArray(therapies) && therapies.length > 0))
      .catch(() => setHasSecondaryTherapies(false))
  }, [secondaryEnabled])

  // Only the sections enabled in the configuration (Terapie Accessorie also needs some therapies)
  const tabs = useMemo(() => resolveAdherenceTabs(sectionsConfig, Boolean(hasSecondaryTherapies)), [sectionsConfig, hasSecondaryTherapies])
  const isActiveTabShown = tabs.some(tab => tab.type === activeTab)

  const filters = filtersByTab[activeTab]

  // One endpoint per tab: only the tab on screen is loaded.
  const fetchAdherence = useCallback(() => {
    if (!isActiveTabShown) return
    const fetcher = activeTab === 'secondary' ? apiClient.getSecondaryTherapiesAdherence : apiClient.getTherapiesAdherence
    fetcher(filters, setStatus)
      .then((response: AdherenceResponse) => {
        setData({therapyType: activeTab, response})
        setDrugNamesByTab(prev => ({...prev, [activeTab]: response.drugNames ?? []}))
      })
      .catch(() => setData(undefined))
  }, [filters, activeTab, isActiveTabShown])

  useEffect(() => {
    fetchAdherence()
  }, [fetchAdherence])

  useEffect(() => {
    if (!isActiveTabShown && tabs.length > 0) setActiveTab(tabs[0].type)
  }, [isActiveTabShown, tabs])

  const visibleFilters = useMemo(() => resolveAdherenceFiltersConfig(configuredFilters), [configuredFilters])
  const filterItemsByTab: PerTab<FilterItem[]> = useMemo(() => ({
    primary: buildAdherenceFilterItems(visibleFilters, defaultRange(), drugNamesByTab.primary),
    secondary: buildAdherenceFilterItems(visibleFilters, defaultRange(), drugNamesByTab.secondary),
  }), [visibleFilters, drugNamesByTab])

  const handleFiltersSubmit = (tab: AdherenceTherapyType, results: FilterResult[]) => {
    setFiltersByTab(prev => ({...prev, [tab]: toAdherenceFilters(results)}))
  }

  const section = data?.therapyType === activeTab ? data.response : undefined
  // every section disabled (or only Terapie Accessorie, for a patient who has none)
  const noSections = tabs.length === 0 && hasSecondaryTherapies !== undefined

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap'}}>
        <Typography variant="body2" color="text.secondary">
          {section ? `Periodo: ${formatDate(section.dateFrom)} – ${formatDate(section.dateTo)}` : ''}
        </Typography>
        {visibleFilters.length > 0 && tabs.map(({type}) => (
          // the inactive tab's drawer stays mounted, so its selection is still there when coming back
          <Box key={type} sx={{display: type === activeTab ? 'block' : 'none'}}>
            <Filters config={filterItemsByTab[type]} onSubmit={results => handleFiltersSubmit(type, results)} />
          </Box>
        ))}
      </Box>

      {noSections && (
        <EmptyState
          title="Nessuna terapia da mostrare"
          description="Non ci sono sezioni di aderenza disponibili."
        />
      )}
      {tabs.length > 0 && <Tabs
        value={isActiveTabShown ? activeTab : tabs[0].type}
        onChange={(_e, value: AdherenceTherapyType) => setActiveTab(value)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
          '& .MuiTabs-indicator': {height: 2},
        }}
      >
        {tabs.map(tab => <Tab key={tab.type} value={tab.type} label={tab.label} />)}
      </Tabs>}

      {!noSections && status === STATUS.PENDING && (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress />
        </Box>
      )}
      {isActiveTabShown && status === STATUS.REJECTED && (
        <InlineMessage severity="error">Impossibile caricare l&apos;aderenza alla terapia.</InlineMessage>
      )}
      {isActiveTabShown && status === STATUS.FULFILLED && section && (
        <SectionCharts section={section} granularity={section.granularity} labels={labels} />
      )}
    </Box>
  )
}
